import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faCheckDouble } from "@fortawesome/free-solid-svg-icons";
import { authenticatedFetch } from "../../utils/api";
import { API_BASE, resolveImageUrl } from "../../config/api";
import useChatSocket from "../../context/ChatSocket/useChatSocket";
import style from "./style.module.css";

interface ChatMessage {
  chat_message_id: number;
  order_id: number;
  sender_user_id: number | null;
  sender_type: string;
  sender_name_snapshot: string;
  message: string | null;
  image_url: string | null;
  created_at: string;
  // Only ever present on the caller's own sent messages — see
  // docs/order-chat-read-receipts-plan.md. Deliberately no "delivered"
  // state, just sent (absent/false) vs read (true).
  is_read_by_recipient?: boolean;
}

interface ChatStatus {
  is_closed: boolean;
  closes_at: string | null;
}

interface OrderChatPanelProps {
  orderId: number;
  onClose: () => void;
}

const OrderChatPanel: FC<OrderChatPanelProps> = ({ orderId, onClose }) => {
  const [cookies] = useCookies();
  const { socket, joinOrder, leaveOrder } = useChatSocket();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [nextBefore, setNextBefore] = useState<number | null>(null);
  const [chatStatus, setChatStatus] = useState<ChatStatus>({ is_closed: false, closes_at: null });
  const [text, setText] = useState("");
  const [image, setImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const listEndRef = useRef<HTMLDivElement>(null);
  const markReadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentUserId = cookies.auth?.user_id;

  // Coalesces a burst of incoming messages into a single PUT /read instead
  // of firing one per message — a rapid run of N incoming messages would
  // otherwise cost N HTTP round trips (and N x markChatRead's own DB
  // queries) for what's logically one "thread just became read" state
  // change. If a call is already scheduled, later messages in the same
  // burst just ride along on it (PUT /read marks every currently-unread
  // message, not one specific id) rather than scheduling their own.
  // Near-identical logic (same 400ms window, same ref+timeout shape) also
  // lives in app_fe/src/views/OrderChatScreen.tsx's scheduleMarkRead — kept
  // as two copies rather than one shared module since these are separate
  // npm/build roots (CRA web app vs. React Native, no workspace/shared-package
  // tooling between them today) and this is ~10 lines; if you change the
  // debounce window or coalescing behavior here, change it there too. See
  // docs/order-chat-read-receipts-plan.md.
  const scheduleMarkRead = useCallback(() => {
    if (markReadTimerRef.current) return;
    markReadTimerRef.current = setTimeout(() => {
      markReadTimerRef.current = null;
      authenticatedFetch(`${API_BASE}/orderchat/${orderId}/read`, { method: "PUT" }, cookies.auth).catch(() => {});
    }, 400);
  }, [orderId, cookies.auth]);

  // Deps include orderId (not just []) — without it, switching to a
  // different order while this panel stays mounted (it's reused, not
  // remounted, across an orderId prop change) would leave a timer scheduled
  // for the PREVIOUS order still pending: it fires ~400ms later and PUTs
  // .../read for the old orderId (the closure captured at schedule time),
  // while the new order's own scheduleMarkRead sees markReadTimerRef.current
  // still truthy and silently skips scheduling — delaying or dropping the
  // new order's read receipt. Depending on orderId makes React run this
  // cleanup (clearing any pending timer) on every orderId change, not just
  // on unmount. Also nulls the ref, not just clearTimeout — clearTimeout
  // alone cancels the timer's callback but leaves markReadTimerRef.current
  // holding the (now-dead) handle, which would still read as truthy and
  // keep blocking the new order's own scheduleMarkRead from ever scheduling.
  useEffect(() => () => {
    if (markReadTimerRef.current) {
      clearTimeout(markReadTimerRef.current);
      markReadTimerRef.current = null;
    }
  }, [orderId]);

  const fetchMessages = useCallback(async (before?: number) => {
    const params = new URLSearchParams({ limit: "50" });
    if (before) params.set("before", String(before));
    const response = await authenticatedFetch(`${API_BASE}/orderchat/${orderId}/messages?${params}`, { method: "GET" }, cookies.auth);
    if (!response.ok) return;
    const result = await response.json();
    setChatStatus(result.chat_status);
    setNextBefore(result.next_before);
    if (before) {
      setMessages((prev) => [...result.data, ...prev]);
    } else {
      setMessages(result.data);
    }
  }, [orderId, cookies.auth]);

  useEffect(() => {
    setIsLoading(true);
    fetchMessages().finally(() => setIsLoading(false));
    // Mark this thread's notifications read once it's opened.
    authenticatedFetch(`${API_BASE}/orderchat/${orderId}/read`, { method: "PUT" }, cookies.auth).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  useEffect(() => {
    joinOrder(orderId);
    return () => leaveOrder(orderId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, socket]);

  useEffect(() => {
    if (!socket) return;
    const handleMessage = (msg: ChatMessage) => {
      if (msg.order_id !== orderId) return;
      setMessages((prev) => (prev.some((m) => m.chat_message_id === msg.chat_message_id) ? prev : [...prev, msg]));
      // A thread that's already open should mark a newly-arrived message
      // read immediately, not just on next mount — otherwise it'd sit at
      // "sent" until the panel is closed and reopened. See
      // docs/order-chat-read-receipts-plan.md.
      if (msg.sender_user_id !== currentUserId) {
        scheduleMarkRead();
      }
    };
    const handleClosed = (payload: { order_id: number }) => {
      if (payload.order_id !== orderId) return;
      setChatStatus((prev) => ({ ...prev, is_closed: true }));
    };
    const handleRead = (payload: { order_id: number; chat_message_ids: number[] }) => {
      if (payload.order_id !== orderId) return;
      setMessages((prev) => prev.map((m) =>
        payload.chat_message_ids.includes(m.chat_message_id) ? { ...m, is_read_by_recipient: true } : m
      ));
    };
    socket.on("chat:message", handleMessage);
    socket.on("chat:closed", handleClosed);
    socket.on("chat:read", handleRead);
    return () => {
      socket.off("chat:message", handleMessage);
      socket.off("chat:closed", handleClosed);
      socket.off("chat:read", handleRead);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, orderId, currentUserId, scheduleMarkRead]);

  useEffect(() => {
    listEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length]);

  const handleImagePick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSend = async () => {
    if (!text.trim() && !image) return;
    setIsSending(true);
    try {
      const response = await authenticatedFetch(`${API_BASE}/orderchat/${orderId}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text.trim() || undefined, image: image || undefined }),
      }, cookies.auth);
      if (response.ok) {
        const created: ChatMessage = await response.json();
        setMessages((prev) => (prev.some((m) => m.chat_message_id === created.chat_message_id) ? prev : [...prev, created]));
        setText("");
        setImage(null);
      }
    } finally {
      setIsSending(false);
    }
  };

  const formatTime = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleTimeString(undefined, {
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const formatClosesAt = (closesAt: string | null) => {
    if (!closesAt) return "";
    const hoursLeft = Math.max(0, Math.round((new Date(closesAt).getTime() - Date.now()) / 3600000));
    return `Chat closes in ${hoursLeft}h`;
  };

  return (
    <div className={style.overlay}>
      <div className={style.panel}>
        <div className={style.header}>
          <h2>Order #{orderId} — Chat</h2>
          <button className={style.closeBtn} onClick={onClose}>×</button>
        </div>

        <div className={style.body}>
          {nextBefore && (
            <button className={style.loadMoreBtn} onClick={() => fetchMessages(nextBefore)}>
              Load older messages
            </button>
          )}
          {isLoading ? (
            <div className={style.stateMessage}>Loading...</div>
          ) : messages.length === 0 ? (
            <div className={style.stateMessage}>No messages yet.</div>
          ) : (
            messages.map((msg) => {
              const isMine = msg.sender_user_id === currentUserId;
              return (
                <div key={msg.chat_message_id} className={`${style.bubbleRow} ${isMine ? style.mine : style.theirs}`}>
                  <div className={style.bubble}>
                    <div className={style.bubbleSender}>{msg.sender_name_snapshot}</div>
                    {msg.message && <div className={style.bubbleText}>{msg.message}</div>}
                    {msg.image_url && <img src={resolveImageUrl(msg.image_url)} alt="attachment" className={style.bubbleImage} />}
                    <div className={style.bubbleTime}>
                      {formatTime(msg.created_at)}
                      {isMine && (
                        <FontAwesomeIcon
                          icon={msg.is_read_by_recipient ? faCheckDouble : faCheck}
                          className={msg.is_read_by_recipient ? style.tickRead : style.tick}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={listEndRef} />
        </div>

        {chatStatus.is_closed ? (
          <div className={style.closedBanner}>This order's chat is closed.</div>
        ) : (
          <div className={style.footer}>
            {chatStatus.closes_at && <div className={style.countdown}>{formatClosesAt(chatStatus.closes_at)}</div>}
            {image && <div className={style.imagePreview}><img src={image} alt="preview" /> <button onClick={() => setImage(null)}>×</button></div>}
            <div className={style.inputRow}>
              <label className={style.attachBtn}>
                📎
                <input type="file" accept="image/*" onChange={handleImagePick} hidden />
              </label>
              <input
                type="text"
                className={style.textInput}
                placeholder="Type a message..."
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSend(); }}
              />
              <button className={style.sendBtn} onClick={handleSend} disabled={isSending || (!text.trim() && !image)}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderChatPanel;
